"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown } from "lucide-react";
import useLocaleStore from "@/stores/useLocaleStore";

const LangSwitcher = () => {
  const { locale, setLocale } = useLocaleStore();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: "en", label: "EN" },
    { code: "am", label: "አማ" }
  ];

  const currentLang = languages.find(lang => lang.code === locale);

  const handleLanguageChange = async (langCode: "en" | "am") => {
    setLocale(langCode);
    setIsOpen(false);
    router.refresh();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setIsOpen(false);
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  // Browser navigation sync
  useEffect(() => {
    const handlePopState = () => router.refresh();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [router]);

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Minimal switcher - fits anywhere */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className="
          group
          flex items-center gap-1.5
          px-3 py-2
          rounded-md
          hover:bg-gray-100 dark:hover:bg-gray-800
          active:bg-gray-200 dark:active:bg-gray-700
          transition-colors duration-150
          focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600
        "
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Current language label */}
        <span className="
          text-sm font-medium
          text-gray-700 dark:text-gray-200
          group-hover:text-gray-900 dark:group-hover:text-gray-100
        ">
          {currentLang?.label}
        </span>
        
        {/* Chevron indicator */}
        <ChevronDown 
          className={`
            w-3.5 h-3.5
            text-gray-500 dark:text-gray-400
            transition-transform duration-200
            ${isOpen ? "rotate-180" : ""}
          `} 
        />
      </button>

      {/* Clean dropdown menu */}
      <div 
        className={`
          absolute top-full mt-1 right-0
          min-w-[80px]
          rounded-md
          bg-white dark:bg-gray-900
          border border-gray-200 dark:border-gray-700
          shadow-md
          overflow-hidden
          z-50
          transition-all duration-200
          origin-top-right
          ${isOpen 
            ? "opacity-100 scale-100 visible" 
            : "opacity-0 scale-95 invisible"
          }
        `}
        role="menu"
        aria-orientation="vertical"
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code as "en" | "am")}
            className={`
              w-full
              px-3 py-2
              text-sm font-medium
              text-left
              transition-colors duration-150
              hover:bg-gray-50 dark:hover:bg-gray-800
              active:bg-gray-100 dark:active:bg-gray-700
              ${locale === lang.code 
                ? "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20" 
                : "text-gray-700 dark:text-gray-300"
              }
            `}
            role="menuitem"
            aria-current={locale === lang.code ? "page" : undefined}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* Touch fallback for mobile */}
      <select
        value={locale}
        onChange={(e) => handleLanguageChange(e.target.value as "en" | "am")}
        className="
          sr-only
        "
        aria-label="Select language"
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LangSwitcher;